/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';

import { isValid as isValidDayDate } from '../../day-date';
import { getDayDate, getISOTimeString } from '../../iso-local-string';
import { isISOTimeString } from '../../iso-time-string';

/**
 * @import { LiteralUnion } from '../../../util.type'
 * @import { CommandType, RawCommand } from '../../command'
 * @import { ISOLocalString } from '../../iso-local-string'
 * @import { JSONObject, JSONValue } from '../../json'
 */

/**
 * Komunikátor příkazů pro Ki-Wi Server.
 */
export default class CommunicatorCommand {
  /**
   * Zpracuje příkaz pro zařízení.
   * @param {CommandServer | JSONValue} json Příkaz pro zařízení ve formátu, který vrací server.
   * @returns {RawCommand} Příkaz pro zařízení.
   * @throws {TypeError} Pokud je příkaz strukturálně nevalidní.
   */
  static parse(json) {
    let cause;
    try {
      const o = /** @type {JSONObject} */ (json);
      if (isPlainObject(o) && isString(o.command) && isString(o.params)) {
        return {
          type: o.command,
          params: CommunicatorCommand.parseParams(o.params)
        };
      }
    } catch (error) {
      cause = error;
    }
    throw new TypeError(
      `structurally invalid command ${JSON.stringify(json)}`,
      { cause }
    );
  }

  /**
   * Rozparsuje parametry příkazu, kde se očekává, že jsou ve stringu obaleném
   * hranatými závorkami a oddělené čárkou, např. `[2024-08-16 09:19, S5]`.
   * @param {LiteralUnion<CommandServer['params'], string>} params
   * @returns {string[]}
   * @throws {TypeError} Pokud je params strukturálně nevalidní
   * (není to string a/nebo nezačíná a nekončí na hranatou závorku).
   */
  static parseParams(params) {
    if (!isString(params) || !params.startsWith('[') || !params.endsWith(']')) {
      throw TypeError(`error parsing params ${JSON.stringify(params)}`);
    }
    // Očekávaný formát je např. "[2024-08-16 09:19, S3]", "[2024-08-16 09:19]".
    const inner = params.slice(1, -1);
    // Speciální ošetření, jelikož split při nenalezení vrací pole o délce jedna.
    if (inner.length === 0) {
      return [];
    }
    return inner.split(', ');
  }
}

/**
 * @typedef CommandServer JSON typ pro obecné příkazy ze serveru.
 * @property {LiteralUnion<CommandType, string>} command Typ příkazu.
 * @property {`[]` | `[${string}]` | `[${string}, ${string}]`} params Konkrétní parametry pro daný příkaz oddělené čárkou.
 * Očekávaný formát je např. "[2024-08-16 09:19, S3]", "[2024-08-16 09:19]".
 *
 * TODO #555: Teoreticky nekonečný počet, ale prakticky chodí max. 2 parametry,
 * proto takový omezený typ.
 */

/**
 * Zparsuje řetězec z (z commandu 'lcd', 'shutdown') do ISOLocalString
 * s očekávaným formátem 'YYYY-MM-DD HH:mm'.
 * @param {string} param
 * @returns {ISOLocalString}
 * @throws {TypeError} Pokud je formát nevalidní.
 */
export function parseCommandDateTimeParam(param) {
  const isoString = /** @type {ISOLocalString} */ (
    `${param.split(' ').join('T')}:00`
  );
  const isoTimeString = getISOTimeString(isoString);
  const dayDate = getDayDate(isoString);
  if (isISOTimeString(isoTimeString) && isValidDayDate(dayDate)) {
    return isoString;
  }
  throw new TypeError(`Invalid ISOLocalString ${JSON.stringify(param)}.`);
}
